<template>
    <div class="tw:flex tw:flex-col tw:gap-5 tw:pb-4">

        <!-- ─── Encabezado del paso ──────────────────────────────────────
             Con pago: resumen de compra tras un pago exitoso.
             Gratuito (precioTotal === 0): cambio de asiento completado. -->
        <div class="tw:rounded-lg tw:border tw:border-green-200 tw:bg-green-50 tw:px-4 tw:py-3">
            <p class="tw:text-sm tw:font-semibold tw:text-green-700 tw:m-0">
                <i class="material-icons tw:text-base tw:align-middle tw:mr-1">check_circle</i>
                {{ $t(precioTotal === 0
                    ? 'perfil.cambio_asiento.cambio_exitoso'
                    : 'perfil.cambio_asiento.pago_exitoso') }}
            </p>
        </div>

        <!-- ─── Comparativa: asiento/plaza ACTUAL vs NUEVA ───────────────── -->
        <div class="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:overflow-hidden">
            <div class="tw:px-4 tw:py-3 tw:bg-gray-50 tw:border-b tw:border-gray-100">
                <h3 class="tw:text-base tw:font-bold tw:text-gray-800 tw:m-0">
                    {{ $t('perfil.cambio_asiento.resumen_titulo_butaca', $t('perfil.cambio_asiento.resumen_titulo')) }}
                </h3>
            </div>

            <div class="tw:px-4 tw:py-3 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-4">

                <!-- Asiento/Plaza ANTIGUA (la que el usuario tenía antes) -->
                <div class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:p-4 tw:rounded-lg tw:bg-gray-50 tw:text-center">
                    <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-gray-100 tw:flex tw:items-center tw:justify-center">
                        <i class="material-icons tw:text-[22px] tw:text-gray-400">{{ esPlaza ? 'airline_seat_recline_normal' : 'event_seat' }}</i>
                    </div>
                    <div>
                        <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1">
                            {{ $t(esPlaza
                                ? 'perfil.cambio_asiento.plaza_antigua'
                                : 'perfil.cambio_asiento.butaca_antigua') }}
                        </p>
                        <p v-if="asientoActual && asientoActual.zona" class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">{{ asientoActual.zona }}</p>
                        <p v-if="asientoActual && !esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            {{ $t('perfil.cambio_asiento.fila') }} {{ asientoActual.fila }}
                            — {{ $t('perfil.cambio_asiento.asiento') }} {{ asientoActual.numero }}
                        </p>
                        <p v-else-if="asientoActual" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            {{ $t('perfil.cambio_asiento.plaza_label', { plaza_id: asientoActual.numero || '—' }) }}
                        </p>
                        <p class="tw:text-xs tw:text-gray-400 tw:mt-1">
                            {{ $t('perfil.cambio_asiento.tipo_abono_actual') }}: {{ tipoAbonoActual }}
                        </p>
                    </div>
                </div>

                <!-- Flecha: mobile ↓, desktop → -->
                <div class="tw:flex tw:md:hidden tw:justify-center">
                    <i class="material-icons tw:text-gray-300 tw:text-lg">arrow_downward</i>
                </div>
                <div class="tw:hidden tw:md:flex tw:md:items-center">
                    <i class="material-icons tw:text-gray-300 tw:text-lg">arrow_forward</i>
                </div>

                <!-- Asiento/Plaza NUEVA -->
                <div class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:p-4 tw:rounded-lg tw:bg-green-50 tw:text-center">
                    <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-green-50 tw:flex tw:items-center tw:justify-center">
                        <i class="material-icons tw:text-[22px] tw:text-green-500">{{ esPlaza ? 'airline_seat_recline_normal' : 'event_seat' }}</i>
                    </div>
                    <div>
                        <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-green-600 tw:mb-1">
                            {{ $t(esPlaza
                                ? 'perfil.cambio_asiento.plaza_nueva'
                                : 'perfil.cambio_asiento.butaca_nueva') }}
                        </p>
                        <p v-if="zonaNueva" class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">{{ zonaNueva }}</p>
                        <p v-if="asientoSeleccionado && !esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            {{ $t('perfil.cambio_asiento.fila') }} {{ asientoSeleccionado.row ?? asientoSeleccionado.fila }}
                            — {{ $t('perfil.cambio_asiento.asiento') }} {{ asientoSeleccionado.number ?? asientoSeleccionado.butaca }}
                        </p>
                        <p v-else-if="asientoSeleccionado && esPlaza" class="tw:text-sm tw:text-gray-500 tw:mb-0">
                            {{ $t('perfil.cambio_asiento.plaza_label', { plaza_id: asientoSeleccionado.number ?? asientoSeleccionado.asiento ?? asientoSeleccionado.numero ?? '—' }) }}
                            <span v-if="asientoSeleccionado.sector_name" class="tw:text-xs tw:text-gray-400">
                                — {{ asientoSeleccionado.sector_name }}
                            </span>
                        </p>
                        <p v-else class="tw:text-sm tw:text-gray-500 tw:mb-0">—</p>
                        <p v-if="tipoAbonoNuevoDescripcion" class="tw:text-xs tw:text-gray-400 tw:mt-1">
                            {{ $t('perfil.cambio_asiento.tipo_abono_nuevo') }}: {{ tipoAbonoNuevoDescripcion }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Importe base del cambio -->
            <div v-if="precioTotal > 0" class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:text-gray-700">{{ $t('perfil.cambio_asiento.importe_cambio') }}</span>
                <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ formatearPrecio(importeCambio) }}</span>
            </div>
            <!-- Gastos de gestión -->
            <div v-if="mostrarGastosGestion && precioTotal > 0" class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:text-gray-700">{{ $t('perfil.cambio_asiento.gastos_gestion') }}</span>
                <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ formatearPrecio(priceManagement) }}</span>
            </div>
            <div class="tw:border-t tw:border-dashed tw:border-gray-300"></div>

            <!-- Total pagado (solo si hay importe; gratuito no muestra nada de dinero) -->
            <div v-if="precioTotal > 0" class="tw:px-4 tw:py-3 tw:flex tw:items-center tw:justify-between tw:bg-gray-50">
                <span class="tw:text-sm tw:font-bold tw:text-gray-800">{{ $t('perfil.cambio_asiento.total') }}</span>
                <span class="tw:text-base tw:font-extrabold tw:text-gray-900">{{ formatearPrecio(precioTotal) }}</span>
            </div>

            <!-- Nº de pedido (si el backend lo devuelve) -->
            <div v-if="numeroPedido" class="tw:px-4 tw:py-2 tw:border-t tw:border-gray-100 tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:text-gray-700">{{ $t('perfil.cambio_asiento.pedido_numero', 'Nº de pedido') }}</span>
                <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ numeroPedido }}</span>
            </div>
        </div>

        <!-- ─── Acciones ──────────────────────────────────────────────────── -->
        <div class="tw:flex tw:items-center tw:justify-end tw:gap-3">
            <Button
                data-testid="cambio-butaca-btn-finalizar"
                variant="alt"
                @click="$emit('completado')"
            >
                <span class="tw:flex tw:items-center tw:gap-1">
                    {{ $t('perfil.cambio_asiento.finalizar', 'Finalizar') }}
                    <i class="material-icons tw:text-base">check</i>
                </span>
            </Button>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import Button from '@/modules/front/ui/Button.vue';

    const props = defineProps({
        /** Detalle completo del abono (asiento/plaza ANTIGUO + tipo de abono). */
        abonoDetail: {
            type: Object,
            default: null,
        },
        /** Asiento o plaza NUEVO seleccionado. */
        asientoSeleccionado: {
            type: Object,
            default: null,
        },
        /** Zona seleccionada (nombre se deriva de aquí). */
        zonaSeleccionada: {
            type: Object,
            default: null,
        },
        /** Descripción del tipo de abono nuevo. */
        tipoAbonoNuevoDescripcion: {
            type: String,
            default: null,
        },
        /** Importe base del cambio (sin gastos de gestión). */
        importeCambio: {
            type: Number,
            default: 0,
        },
        /** Gastos de gestión del cambio. */
        priceManagement: {
            type: Number,
            default: 0,
        },
        /** Precio total pagado (0 = gratuito). */
        precioTotal: {
            type: Number,
            default: 0,
        },
        /** Si mostrar o no la línea de gastos de gestión (priceManagement > 0). */
        mostrarGastosGestion: {
            type: Boolean,
            default: false,
        },
        /**
         * Resultado del pago Paycomet (`get-pedido-procesado-paycomet`).
         * Opcional; de ahí se lee `n_pedido` si viene.
         */
        pagoResultado: {
            type: Object,
            default: null,
        },
        /**
         * Resultado de la confirmación del cambio (gratuito o con pago).
         * Opcional; contiene `n_pedido` y `session_id` devueltos por el backend
         * al confirmar. Para el CAMINO GRATUITO (`precioTotal === 0`) el
         * `pagoResultado` queda en `null` (no hay pasarela), pero el backend
         * sigue devolviendo `n_pedido` aquí. Sirve como fallback para que el
         * nº de pedido se muestre también en el resumen gratuito.
         */
        resultadoCambio: {
            type: Object,
            default: null,
        },
    });

    const emit = defineEmits([
        /** Cierra el flujo (el padre emite `completado` al PerfilAbonosView). */
        'completado',
    ]);

    /**
     * ¿El asiento/plaza NUEVA es una plaza? (flag canonical per-zona o per-seat).
     */
    const esPlaza = computed(() => {
        if (props.zonaSeleccionada?.tiene_plazas === true) return true;
        if (props.asientoSeleccionado?.es_plaza === true) return true;
        return false;
    });

    /**
     * Datos del asiento/plaza ANTIGUO (del detalle del abono). Reutiliza el
     * patrón de CambioButacaPasoConfirmacion: el backend puede devolver el
     * asiento como `asiento` o como `plaza`.
     */
    const asientoActual = computed(() => {
        if (!props.abonoDetail) return null;
        const src = props.abonoDetail.asiento
            ?? props.abonoDetail.plaza
            ?? props.abonoDetail.abonado?.asiento
            ?? props.abonoDetail.abonado?.plaza
            ?? props.abonoDetail.data?.asiento
            ?? props.abonoDetail.data?.plaza
            ?? null;
        if (!src) return null;
        return {
            zona: (typeof src.region === 'object' ? src.region?.nombre : src.region) ?? src.zona ?? null,
            fila: src.fila ?? src.row ?? '—',
            numero: src.asiento ?? src.seat ?? src.numero ?? src.butaca ?? '—',
        };
    });

    /** Tipo de abono ANTIGUO (descripción del precio del abono). */
    const tipoAbonoActual = computed(() => {
        return props.abonoDetail?.precio?.descripcion ?? '—';
    });

    /** Nombre de la zona/región NUEVA seleccionada. */
    const zonaNueva = computed(() => {
        const z = props.zonaSeleccionada;
        if (!z) return null;
        return z.nombre ?? z.name ?? z.descripcion ?? null;
    });

    /**
     * Nº de pedido a mostrar en el resumen. Tres fuentes, en orden:
     *  1. `pagoResultado.n_pedido` (Paycomet): el backend del TPV devuelve
     *     `n_pedido` en `get-pedido-procesado-paycomet`.
     *  2. `pagoResultado.nPedido` (alias legacy camelCase del backend Paycomet).
     *  3. `resultadoCambio.n_pedido` (fallback para cambio GRATUITO): en ese
     *     camino `pagoResultado` queda en `null` (no hay pasarela), pero el
     *     backend sigue devolviendo `n_pedido` al confirmar el cambio. Sin este
     *     fallback el resumen gratuito no muestra el nº de pedido.
     */
    const numeroPedido = computed(() => {
        return props.pagoResultado?.n_pedido
            ?? props.pagoResultado?.nPedido
            ?? props.resultadoCambio?.n_pedido
            ?? null;
    });

    /** Formatea un número como precio en euros. */
    function formatearPrecio(val) {
        const n = parseFloat(val);
        if (isNaN(n)) return '—';
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
        }).format(n);
    }
</script>